import React, { useMemo, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Gauge, Table2, BarChart3, Trophy } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';

// The numbers that show how a live ad is doing (worked out on the server; see utils/meta_mapping.py).
const rupees = (paise) => (paise == null ? '–' : `₹${(paise / 100).toLocaleString('en-IN', { maximumFractionDigits: paise < 10000 ? 2 : 0 })}`);
const KEY_NUMBERS = [
  { key: 'cost_per_result_paise', label: 'Cost per result', help: 'What each call, message, visit or lead cost', show: rupees },
  { key: 'click_rate_percent', label: 'Click rate', help: 'Of every 100 people shown the ad, how many tapped it', show: v => (v == null ? '–' : `${v}%`) },
  { key: 'cost_per_click_paise', label: 'Cost per click', help: 'What each tap on the ad cost', show: rupees },
  { key: 'cost_per_1000_views_paise', label: 'Per 1,000 views', help: 'What it cost to show the ad 1,000 times', show: rupees },
  { key: 'frequency', label: 'Times seen', help: 'How many times each person saw the ad, on average', show: v => (v == null ? '–' : `${v}×`) },
];

// Chart marks use the theme's strong brand colour (passes 3:1 contrast on the card in light and dark).
function useMarkColor() {
  const { theme } = useTheme();
  return useMemo(() => {
    const v = getComputedStyle(document.documentElement).getPropertyValue('--brand-strong').trim();
    return v ? `hsl(${v})` : '#b04507';
  }, [theme]); // eslint-disable-line react-hooks/exhaustive-deps
}

const shortDay = (day) => new Date(`${day}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });

function DayChart({ title, data, dataKey, format, color }) {
  return (
    <div>
      <p className="text-xs font-semibold mb-1">{title}</p>
      <div className="h-32" role="img" aria-label={`${title}, by day`}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: -12 }} barCategoryGap={2}>
            <CartesianGrid vertical={false} stroke="hsl(var(--border))" strokeWidth={1} />
            <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} interval="preserveStartEnd" />
            <YAxis tickLine={false} axisLine={false} allowDecimals={false} width={44} tickFormatter={v => format(v, true)}
              tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} />
            <Tooltip cursor={{ fill: 'hsl(var(--muted))' }} formatter={v => [format(v), title]}
              contentStyle={{ background: 'hsl(var(--card))', border: '2px solid hsl(var(--ink))', borderRadius: 8, fontSize: 12 }}
              labelStyle={{ color: 'hsl(var(--foreground))', fontWeight: 600 }} itemStyle={{ color: 'hsl(var(--foreground))' }} />
            <Bar dataKey={dataKey} fill={color} radius={[4, 4, 0, 0]} maxBarSize={24} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function AdColumn({ name, ad, better }) {
  const m = ad?.metrics || {};
  return (
    <div className="flex-1 min-w-0 p-2.5 rounded-lg bg-butter/40 border-2 border-ink/15">
      <p className="text-xs font-semibold mb-1.5 flex items-center gap-1">
        {name}
        {better && <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-money"><Trophy className="w-3 h-3" aria-hidden="true" /> Cheaper</span>}
      </p>
      <dl className="space-y-1 text-xs">
        {[['Views', (ad?.impressions || 0).toLocaleString('en-IN')], ['Clicks', (ad?.clicks || 0).toLocaleString('en-IN')],
          ['Results', (ad?.results || 0).toLocaleString('en-IN')], ['Cost per result', rupees(m.cost_per_result_paise)],
          ['Click rate', m.click_rate_percent == null ? '–' : `${m.click_rate_percent}%`]].map(([k, v]) => (
          <div key={k} className="flex justify-between gap-2"><dt className="text-muted-foreground">{k}</dt><dd className="font-semibold font-mono">{v}</dd></div>
        ))}
      </dl>
    </div>
  );
}

const VERDICTS = {
  cheaper: { text: 'cheaper than most', cls: 'text-money' },
  similar: { text: 'about the same', cls: 'text-foreground' },
  pricier: { text: 'costing more than most', cls: 'text-amber-700 dark:text-amber-400' },
};

// What similar businesses typically pay per result, and how this ad compares (services/benchmarks.py).
function Benchmark({ b }) {
  const v = VERDICTS[b.verdict];
  return (
    <p className="text-xs p-2.5 rounded-lg bg-butter/40 border-2 border-ink/15" data-testid="ad-benchmark">
      Similar ads ({b.scope}, {b.ads} ads) pay about <b>{rupees(b.cpr_paise)}</b> per result.
      {v && <> Yours: <b>{rupees(b.yours_paise)}</b>, <span className={`font-semibold ${v.cls}`}>{v.text}</span>.</>}
    </p>
  );
}

const TEXT_KINDS = [['headline', 'Headlines'], ['primary_text', 'Main texts'], ['description', 'Descriptions']];

// How each of the ad's text options did: Meta mixes them, so this shows which words bring results cheapest.
function TextResults({ texts }) {
  const kinds = TEXT_KINDS.filter(([k]) => (texts[k] || []).length > 1);
  if (!kinds.length) return null;
  return (
    <div data-testid="text-results">
      <p className="text-xs font-semibold mb-1.5">Which words worked</p>
      <div className="space-y-3">
        {kinds.map(([k, label]) => {
          const rows = texts[k].map(t => ({ ...t, cpr: t.results ? t.spend_paise / t.results : null }));
          const costs = rows.map(r => r.cpr).filter(c => c != null);
          const best = costs.length > 1 ? Math.min(...costs) : null;
          return (
            <div key={k}>
              <p className="text-[11px] text-muted-foreground mb-1">{label}</p>
              <ul className="space-y-1">
                {rows.map((r, i) => (
                  <li key={i} className="p-2 rounded-lg bg-butter/40 border-2 border-ink/15 text-xs flex items-start justify-between gap-3">
                    <span className="min-w-0 break-words">{r.text}</span>
                    <span className="shrink-0 text-right font-mono">
                      {r.results} result{r.results === 1 ? '' : 's'}{r.cpr != null && <> · {rupees(Math.round(r.cpr))}</>}
                      {best != null && r.cpr === best && r.results >= 3 && (
                        <span className="flex items-center justify-end gap-0.5 text-[10px] font-semibold text-money font-sans"><Trophy className="w-3 h-3" aria-hidden="true" /> Best</span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-muted-foreground mt-1">Meta shows the words that work more often. Your next ads learn from the winners.</p>
    </div>
  );
}

export default function AdMetrics({ campaign }) {
  const [asTable, setAsTable] = useState(false);
  const color = useMarkColor();
  const perf = campaign.performance || {};
  const metrics = perf.metrics || {};
  const daily = (perf.daily || []).map(d => ({ ...d, label: shortDay(d.day) }));
  const budget = campaign.budget_paise || 0;
  const spent = perf.spend_used_paise || 0;
  const daysLeft = campaign.end_at && ['live', 'paused', 'in_meta_review'].includes(campaign.status)
    ? Math.max(0, Math.ceil((new Date(campaign.end_at) - Date.now()) / 86400000)) : null;
  const { image, video } = perf.ads || {};
  const cheaperOf = (a, b, names) => {
    const c = [a?.metrics?.cost_per_result_paise, b?.metrics?.cost_per_result_paise];
    return c.every(x => x != null) && c[0] !== c[1] ? (c[0] < c[1] ? names[0] : names[1]) : null;
  };
  const cheaper = cheaperOf(image, video, ['image', 'video']);
  if (!perf.impressions && !perf.metrics) return null;

  return (
    <div className="p-4 rounded-xl bg-card border-2 border-ink space-y-4" data-testid="ad-metrics">
      <h3 className="text-sm font-semibold flex items-center gap-2"><Gauge className="w-4 h-4 text-muted-foreground" /> Key numbers</h3>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
        {KEY_NUMBERS.map(k => (
          <div key={k.key} className="p-2.5 rounded-lg bg-butter/50 border-2 border-ink/15 min-w-0" title={k.help}>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">{k.label}</p>
            <p className="text-lg font-bold font-mono break-all">{k.show(metrics[k.key])}</p>
          </div>
        ))}
      </div>

      {budget > 0 && (
        <div>
          <div className="flex flex-wrap justify-between gap-x-3 text-xs mb-1">
            <span className="text-muted-foreground">Spent <b className="text-foreground">{rupees(spent)}</b> of {rupees(budget)}</span>
            {daysLeft != null && <span className="text-muted-foreground">{daysLeft} day{daysLeft === 1 ? '' : 's'} left</span>}
          </div>
          <div className="h-2.5 rounded-full bg-muted border border-ink/20 overflow-hidden" role="progressbar"
            aria-valuemin={0} aria-valuemax={budget} aria-valuenow={spent} aria-label="Budget spent">
            <div className="h-full rounded-full" style={{ width: `${Math.min(100, (100 * spent) / budget)}%`, background: color }} />
          </div>
        </div>
      )}

      {daily.length > 0 && (
        <div>
          <div className="flex justify-end mb-1">
            <button type="button" onClick={() => setAsTable(t => !t)} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
              {asTable ? <><BarChart3 className="w-3.5 h-3.5" /> Show charts</> : <><Table2 className="w-3.5 h-3.5" /> Show as table</>}
            </button>
          </div>
          {asTable ? (
            <div className="overflow-x-auto"><table className="w-full min-w-[320px] text-xs">
              <thead><tr className="text-muted-foreground text-left"><th className="font-medium py-1">Day</th><th className="font-medium text-right">Views</th><th className="font-medium text-right">Clicks</th><th className="font-medium text-right">Results</th><th className="font-medium text-right">Spent</th></tr></thead>
              <tbody>
                {daily.map(d => (
                  <tr key={d.day} className="border-t border-border"><td className="py-1">{d.label}</td><td className="text-right font-mono">{d.impressions}</td><td className="text-right font-mono">{d.clicks}</td><td className="text-right font-mono">{d.results}</td><td className="text-right font-mono">{rupees(d.spend_paise)}</td></tr>
                ))}
              </tbody>
            </table></div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 [&>*]:min-w-0">
              <DayChart title="Results per day" data={daily} dataKey="results" color={color}
                format={v => Number(v).toLocaleString('en-IN')} />
              <DayChart title="Spend per day" data={daily} dataKey="spend_paise" color={color}
                format={(v, axis) => (axis ? `₹${Math.round(v / 100).toLocaleString('en-IN')}` : rupees(v))} />
            </div>
          )}
        </div>
      )}

      {campaign.benchmark && <Benchmark b={campaign.benchmark} />}

      {image && video && (
        <div>
          <p className="text-xs font-semibold mb-1.5">Image ad vs video ad</p>
          <div className="flex flex-col sm:flex-row gap-2.5">
            <AdColumn name="Image ad" ad={image} better={cheaper === 'image'} />
            <AdColumn name="Video ad" ad={video} better={cheaper === 'video'} />
          </div>
        </div>
      )}

      {perf.texts && <TextResults texts={perf.texts} />}

      {metrics.frequency == null && <p className="text-xs text-muted-foreground">More numbers show up here within 15 minutes of your ad starting.</p>}
    </div>
  );
}
